import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  AuthShell,
  AuthField,
  AuthPrimaryButton,
  AuthErrorBanner,
  formatAuthErrorDetail,
} from "@/components/auth/AuthShell";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Please request a new one.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      toast.success("Password updated. Welcome back.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(
        formatAuthErrorDetail(err.response?.data?.detail) ||
          "Reset failed. Please request a new link."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Set new password"
      title="Choose a new password."
      subtitle="This will sign you out of every other device — a fresh start."
      footer={
        <>
          Changed your mind?{" "}
          <Link
            to="/login"
            data-testid="reset-cancel-link"
            className="text-[#E8B96C] underline underline-offset-4 decoration-[#E8B96C]/40 hover:decoration-[#E8B96C]"
          >
            Back to sign in
          </Link>
        </>
      }
    >
      <AuthErrorBanner error={error} testId="reset-error" />
      <form onSubmit={onSubmit} className="space-y-5">
        <AuthField
          label="New password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder="At least 8 characters"
          autoComplete="new-password"
          testId="reset-password-input"
          hint="Min 8 chars. Signs out every other device on submit."
        />
        <AuthField
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={setConfirm}
          placeholder="Retype it"
          autoComplete="new-password"
          testId="reset-confirm-input"
        />
        <AuthPrimaryButton disabled={busy || !token} testId="reset-submit-button">
          {busy ? "Updating…" : "Set new password"}
        </AuthPrimaryButton>
      </form>
    </AuthShell>
  );
}
